import React, { useEffect, useRef, useState } from 'react';
import { ActionIcon, Button, FileButton, FileInput, Modal, NumberInput, PasswordInput, Select, TextInput, Tooltip } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBan, faCamera, faCircleCheck, faMagnifyingGlass, faPen, faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import type { EmployeeDepartment } from 'shared-models/browser';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import { Button as SharedButton } from '../../../../components/Button';
import { employeesService, type EmployeeView } from '../../../../services/employees/employees.service';
import { toHebrewApiErrorMessage } from '../../../../services/index/index.service';
import { useEmployeeSession } from '../../../../contexts/EmployeeSessionProvider';
import { useAuth } from '../../../../contexts/AuthProvider';
import { matchesSearchQuery, normalizeSearchText } from '../../../../utils/semantic-search.util';
import settingsClasses from './shared/settings-shared.module.scss';
import navyModal from '../../../../components/Modal/navy-modal.module.scss';
import classes from './pos-employees-settings-content.module.scss';
import { AttendanceFaceEnrollmentModal } from './AttendanceFaceEnrollmentModal';

type EmployeeFormState = { fullName: string; pin: string; role: 'manager' | 'employee'; baseHourlySalary: string };

type PosEmployeesSettingsContentProps = {
  form: EmployeeFormState;
  photoFile: File | null;
  editingEmployeeId: number | null;
  settingsEmployees: EmployeeView[];
  isLoadingSettings: boolean;
  isSavingEmployee: boolean;
  attendanceUiEnabled: boolean;
  onChangeForm: (key: keyof EmployeeFormState, value: string) => void;
  onPhotoFileChange: (file: File | null) => void;
  onRefreshEmployees: () => void;
  onResetForm: () => void;
  onSaveEmployee: (event: React.FormEvent<HTMLFormElement>) => void;
  onEditEmployee: (employee: EmployeeView) => void;
  onDeactivateEmployee: (employee: EmployeeView) => void;
  onActivateEmployee: (employee: EmployeeView) => void;
};

const notifyError = (error: unknown) => notifications.show({ color: 'red', message: toHebrewApiErrorMessage((error as Error).message) });

// Architectural summary: template download + xlsx upload; the list itself updates via SSE upserts.
const useEmployeesXlsxImport = () => {
  const { employeeState } = useEmployeeSession();
  const tenantId = employeeState?.employee.tenantId ?? '';
  const employeeSession = employeeState?.employeeSession ?? '';
  const [isImporting, setImporting] = useState(false);
  const importInFlightRef = useRef(false);
  const resetFileRef = useRef<() => void>(null);

  const downloadTemplate = async () => {
    try {
      const url = URL.createObjectURL(await employeesService.downloadImportTemplate(tenantId, employeeSession));
      const link = document.createElement('a');
      link.href = url;
      link.download = 'employees-import-template.xlsx';
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 0);
    } catch (error: unknown) {
      notifyError(error);
    }
  };

  const importFile = async (file: File | null) => {
    if (!file || importInFlightRef.current) return;
    importInFlightRef.current = true;
    setImporting(true);
    try {
      const result = await employeesService.importXlsx(tenantId, file, employeeSession);
      const reason = result.skipped > 0 && result.firstSkippedReason ? ` (${result.firstSkippedReason})` : '';
      notifications.show({ color: 'green', message: `יובאו ${result.created} עובדים חדשים, עודכנו ${result.updated}, ללא שינוי ${result.unchanged}, דולגו ${result.skipped}${reason}` });
    } catch (error: unknown) {
      notifyError(error);
    } finally {
      importInFlightRef.current = false;
      setImporting(false);
      resetFileRef.current?.();
    }
  };

  return { isImporting, resetFileRef, downloadTemplate, importFile };
};

const PosEmployeesSettingsContentComponent: React.FC<PosEmployeesSettingsContentProps> = ({
  form, photoFile, editingEmployeeId, settingsEmployees, isLoadingSettings, isSavingEmployee, attendanceUiEnabled,
  onChangeForm, onPhotoFileChange, onRefreshEmployees, onResetForm, onSaveEmployee, onEditEmployee,
  onDeactivateEmployee, onActivateEmployee,
}) => {
  const [search, setSearch] = useState('');
  const [faceEmployee, setFaceEmployee] = useState<{ id: number; fullName: string } | null>(null);
  const xlsxImport = useEmployeesXlsxImport();
  const { user } = useAuth();
  const query = normalizeSearchText(search);
  const visibleEmployees = settingsEmployees.filter((employee) =>
    (employee.isActive || user?.role === 'super_admin')
    && (query.length < 2 || matchesSearchQuery(query, [normalizeSearchText(employee.fullName)])),
  );
  const [isCreateModalOpen, setCreateModalOpen] = useState(false);
  const isFormModalOpen = isCreateModalOpen || editingEmployeeId !== null;
  const isEditMode = editingEmployeeId !== null;
  const isEmployeeEditorTabletFullscreen = useMediaQuery('(max-width: 1200px)', false);
  const wasSavingEmployeeRef = useRef(false);
  const [departments, setDepartments] = useState<EmployeeDepartment[]>([]);
  useEffect(() => {
    if (!isFormModalOpen) return;
    const employee = settingsEmployees.find((row) => row.id === editingEmployeeId);
    setDepartments((employee?.departments ?? [{ name: 'כללי', hourlySalary: employee?.baseHourlySalary ?? null }]).map((department) => ({ ...department })));
  }, [isFormModalOpen, editingEmployeeId]);

  useEffect(() => {
    if (wasSavingEmployeeRef.current && !isSavingEmployee && !isEditMode && isCreateModalOpen) setCreateModalOpen(false);
    wasSavingEmployeeRef.current = isSavingEmployee;
  }, [isCreateModalOpen, isEditMode, isSavingEmployee]);

  const closeFormModal = () => {
    if (isSavingEmployee) return;
    setCreateModalOpen(false);
    onResetForm();
  };

  return (
    <section className={settingsClasses.section}>
      <TextInput
        data-testid="employees-settings-search"
        aria-label="חיפוש עובדים"
        placeholder="חיפוש עובדים"
        value={search}
        onChange={(event) => setSearch(event.currentTarget.value)}
        leftSection={<FontAwesomeIcon icon={faMagnifyingGlass as IconProp} />}
        radius="md"
        onContextMenu={(event) => event.preventDefault()}
        styles={{ input: { userSelect: 'none', WebkitUserSelect: 'none', WebkitTouchCallout: 'none' } }}
      />
      <div className={settingsClasses.toolbar}>
        <div className={settingsClasses.formActions}>
          <SharedButton type="button" variant="save" size="sm" onClick={() => setCreateModalOpen(true)} dataTestId="employees-settings-create-open">צור עובד</SharedButton>
          <Button type="button" variant="default" radius="md" data-testid="employees-settings-refresh" onClick={onRefreshEmployees} disabled={isLoadingSettings}>{isLoadingSettings ? 'מרענן...' : 'רענן'}</Button>
          <Button type="button" variant="default" radius="md" data-testid="employees-import-template" onClick={() => { void xlsxImport.downloadTemplate(); }}>הורד דוגמה</Button>
          <FileButton resetRef={xlsxImport.resetFileRef} accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" inputProps={{ 'data-testid': 'employees-import-file' } as Record<string, string>} onChange={(file) => { void xlsxImport.importFile(file); }}>
            {(fileButtonProps) => <Button type="button" variant="default" radius="md" data-testid="employees-import-button" onClick={fileButtonProps.onClick} disabled={xlsxImport.isImporting}>{xlsxImport.isImporting ? 'מייבא...' : 'ייבוא'}</Button>}
          </FileButton>
        </div>
      </div>
      {faceEmployee && <AttendanceFaceEnrollmentModal key={faceEmployee.id} employeeId={faceEmployee.id} employeeName={faceEmployee.fullName} onClose={() => setFaceEmployee(null)} />}
      <div className={classes.grid} data-testid="employees-settings-cards">
        {visibleEmployees.map((employee) => (
          <article key={employee.id} className={classes.card} data-testid={`employees-settings-card-${employee.id}`}>
            <div className={classes.cardHeader}>
              {employee.photoUrl ? <img src={employee.photoUrl} alt={`${employee.fullName} profile`} className={classes.photo} /> : <span className={classes.initial}>{employee.fullName.trim().charAt(0).toUpperCase() || '?'}</span>}
              <div className={classes.identity} data-testid={`employees-settings-details-${employee.id}`}>
                <strong>{employee.fullName}</strong>
                {attendanceUiEnabled && (employee.departments && (employee.departments.length > 1 || employee.departments[0]?.name !== 'כללי')
                  ? employee.departments.map((department) => <span key={department.name} className={classes.salary}>{department.name} <strong>{department.hourlySalary ?? '—'}</strong></span>)
                  : <span className={classes.salary}>שכר שעתי <strong>{employee.baseHourlySalary == null ? '—' : `${employee.baseHourlySalary}`}</strong></span>)}
                <span>{employee.role === 'manager' ? 'מנהל' : 'עובד'}</span>
              </div>
              <div className={classes.statusActions}>
                <span className={employee.isActive ? settingsClasses.statusActive : settingsClasses.statusInactive}>{employee.isActive ? 'פעיל' : 'לא פעיל'}</span>
                <div className={classes.actions}>
                  {attendanceUiEnabled && employee.isActive && <Tooltip label="רישום פנים לנוכחות" withArrow>
                    <ActionIcon aria-label={`רישום פנים ${employee.fullName}`} variant="subtle" color="gray" size="lg" onClick={() => setFaceEmployee(employee)}>
                      <FontAwesomeIcon icon={faCamera as IconProp} />
                    </ActionIcon>
                  </Tooltip>}
                  <Tooltip label="ערוך" withArrow><ActionIcon aria-label="ערוך" data-testid={`employees-settings-edit-${employee.id}`} variant="subtle" color="gray" radius="md" size="lg" onClick={() => onEditEmployee(employee)}><FontAwesomeIcon icon={faPen as IconProp} /></ActionIcon></Tooltip>
                  {employee.isActive ? (
                    <Tooltip label="השבת" withArrow><ActionIcon aria-label="השבת" data-testid={`employees-settings-toggle-${employee.id}`} variant="subtle" color="red" radius="md" size="lg" onClick={() => onDeactivateEmployee(employee)}><FontAwesomeIcon icon={faBan as IconProp} /></ActionIcon></Tooltip>
                  ) : (
                    <Tooltip label="הפעל" withArrow><ActionIcon aria-label="הפעל" data-testid={`employees-settings-toggle-${employee.id}`} variant="subtle" color="green" radius="md" size="lg" onClick={() => onActivateEmployee(employee)}><FontAwesomeIcon icon={faCircleCheck as IconProp} /></ActionIcon></Tooltip>
                  )}
                </div>
              </div>
            </div>
          </article>
        ))}
        {settingsEmployees.length === 0 && !isLoadingSettings && <p className={classes.empty}>אין עובדים לטננט זה עדיין.</p>}
        {settingsEmployees.length > 0 && visibleEmployees.length === 0 && !isLoadingSettings && <p className={classes.empty}>לא נמצאו עובדים</p>}
      </div>
      <Modal opened={isFormModalOpen} onClose={closeFormModal} title={isEditMode ? 'עריכת עובד' : 'עובד חדש'} size={isEmployeeEditorTabletFullscreen ? '100vw' : 'lg'} centered={!isEmployeeEditorTabletFullscreen} fullScreen={isEmployeeEditorTabletFullscreen} radius="md" classNames={{ root: `${navyModal.root} ${classes.employeeEditorModal} ${isEmployeeEditorTabletFullscreen ? classes.employeeEditorTabletFullscreenRoot : ''}` }} closeButtonProps={{ disabled: isSavingEmployee, 'data-testid': 'employees-settings-form-close' } as React.ComponentProps<typeof Modal>['closeButtonProps']} closeOnClickOutside={!isSavingEmployee} closeOnEscape={!isSavingEmployee}>
        <form autoComplete="off" data-form-type="other" onSubmit={onSaveEmployee} className={`${settingsClasses.section} ${isEmployeeEditorTabletFullscreen ? classes.employeeEditorForm : ''}`} onContextMenu={(event) => event.preventDefault()}>
          <div className={classes.employeeEditorFields}>
            <TextInput name="employee-display-name" autoComplete="off" data-lpignore="true" data-1p-ignore data-testid="employees-settings-name" label="שם מלא" placeholder="שם מלא" value={form.fullName} onChange={(event) => onChangeForm('fullName', event.currentTarget.value)} minLength={2} maxLength={120} required radius="md" />
            <PasswordInput name="employee-access-code" autoComplete="new-password" data-lpignore="true" data-1p-ignore data-testid="employees-settings-pin" label={isEditMode ? 'קוד עובד חדש (אופציונלי)' : 'קוד עובד'} placeholder={isEditMode ? 'קוד עובד חדש (אופציונלי)' : 'קוד עובד'} value={form.pin} onChange={(event) => onChangeForm('pin', event.currentTarget.value)} minLength={isEditMode ? undefined : 4} maxLength={10} required={!isEditMode} radius="md" classNames={{ innerInput: classes.employeeEditorInput }} />
            <Select data-testid="employees-settings-role" label="תפקיד" data={[{ value: 'employee', label: 'עובד' }, { value: 'manager', label: 'מנהל' }]} value={form.role} onChange={(value) => onChangeForm('role', (value ?? 'employee') as 'manager' | 'employee')} radius="md" />
            {attendanceUiEnabled && <div>
              <input type="hidden" name="employee-departments" value={JSON.stringify(departments)} data-testid="employees-settings-departments-value" />
              {departments.map((department, index) => <div key={index} className={classes.departmentRow}>
                <TextInput label="מחלקה" aria-label={`מחלקה ${index + 1}`} value={department.name} maxLength={80}
                  onChange={(event) => { const name = event.currentTarget.value; setDepartments((rows) => rows.map((row, rowIndex) => rowIndex === index ? { ...row, name } : row)); }}
                  onContextMenu={(event) => event.preventDefault()} styles={{ input: { userSelect: 'none', WebkitUserSelect: 'none', WebkitTouchCallout: 'none' } }} />
                <NumberInput label="שכר שעתי" aria-label={`שכר מחלקה ${index + 1}`} min={0} max={1_000_000} decimalScale={2}
                  data-testid={index === 0 ? 'employees-settings-hourly-salary' : `employees-settings-department-salary-${index}`}
                  onContextMenu={(event) => event.preventDefault()} styles={{ input: { userSelect: 'none', WebkitUserSelect: 'none', WebkitTouchCallout: 'none' } }}
                  value={department.hourlySalary ?? ''} onChange={(value) => {
                    setDepartments((rows) => rows.map((row, rowIndex) => rowIndex === index ? { ...row, hourlySalary: typeof value === 'number' ? value : null } : row));
                    if (department.name === 'כללי') onChangeForm('baseHourlySalary', String(value));
                  }} />
                <Tooltip label="הסר מחלקה"><ActionIcon type="button" aria-label={`הסר מחלקה ${index + 1}`} disabled={departments.length === 1 || isSavingEmployee}
                  onClick={() => setDepartments((rows) => rows.filter((_, rowIndex) => rowIndex !== index))}><FontAwesomeIcon icon={faTrash as IconProp} /></ActionIcon></Tooltip>
              </div>)}
              <Tooltip label="הוסף מחלקה"><ActionIcon type="button" aria-label="הוסף מחלקה" disabled={departments.length >= 30 || isSavingEmployee}
                onClick={() => setDepartments((rows) => [...rows, { name: '', hourlySalary: null }])}><FontAwesomeIcon icon={faPlus as IconProp} /></ActionIcon></Tooltip>
            </div>}
          </div>
          <div className={settingsClasses.fileField}>
            <label className={`${settingsClasses.fileFieldLabel} ${classes.employeeEditorFieldLabel}`}>תמונת עובד</label>
            <FileInput data-testid="employees-settings-photo" accept="image/jpeg,image/webp" onChange={onPhotoFileChange} placeholder="בחר קובץ" aria-label="Employee Photo" radius="md" clearable />
            {photoFile && <span className={settingsClasses.lead}>תמונה נבחרה: {photoFile.name}</span>}
          </div>
          <div className={`${settingsClasses.formActions} ${isEmployeeEditorTabletFullscreen ? classes.employeeEditorFooter : ''}`}>
            <Button type="button" variant="default" radius="md" onClick={closeFormModal} disabled={isSavingEmployee} data-testid="employees-settings-cancel-edit">ביטול</Button>
            <SharedButton type="submit" variant="save" size="sm" disabled={isSavingEmployee} dataTestId="employees-settings-submit">{isSavingEmployee ? 'שומר...' : isEditMode ? 'שמור עובד' : 'צור עובד'}</SharedButton>
          </div>
        </form>
      </Modal>
    </section>
  );
};

export const PosEmployeesSettingsContent = React.memo(PosEmployeesSettingsContentComponent);
